import { useEffect, useState, useMemo } from 'react';
import { Job, JobFilters, JobSearchResult } from '../types/job';
import { fetchJobs } from '../api/jobApi';
import { filterJobs } from '../utils/jobFilters';

export function useJobSearch(
  searchTerm: string,
  filters: JobFilters
): JobSearchResult {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = 6;

  // Fetch jobs only once when the component mounts
  useEffect(() => {
    let mounted = true;

    async function loadJobs() {
      try {
        setLoading(true);
        const data = await fetchJobs();
        
        if (!mounted) return;

        // Sort jobs by creation date (newest first)
        const sortedJobs = data.sort((a, b) => {
          const dateA = new Date(a.created_at || 0).getTime();
          const dateB = new Date(b.created_at || 0).getTime();
          return dateB - dateA;
        });
        
        setJobs(sortedJobs);
      } catch (err) {
        if (!mounted) return;
        console.error('Error fetching jobs:', err);
        setError('Impossible de charger les offres. Veuillez réessayer plus tard.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadJobs();

    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array means this effect runs once on mount

  // Memoize filtered jobs to prevent unnecessary recalculations
  const filteredJobs = useMemo(() => {
    return filterJobs(jobs, searchTerm, filters);
  }, [jobs, searchTerm, filters]);

  return {
    jobs,
    filteredJobs,
    loading,
    error,
    page,
    itemsPerPage,
    setPage,
  };
}